import { Box, Button } from '@chakra-ui/react';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGet3thPartyServices, useGetOneService } from 'shared/api/requests/services';
import Card from 'shared/components/horizon-ui/card/Card';
import { HSeparator } from 'shared/components/horizon-ui/separator/Separator';
import Link from 'shared/components/Link';
import Loader from 'shared/components/Loader';
import {
  updateActorSettingResolver,
  UpdateActorSettingFormT,
  getUpdateActorSettingValues,
} from 'shared/form-values/actor-settings';
import useGetColors from 'shared/hooks/useGetColors';
import { PathsE, PathsParams } from 'shared/routes';
import { fillFormErrors } from 'shared/utils/devUtils';
import Form from './Form';
import PageTitle from 'shared/views/admin/PageTitle';

const Update = () => {
  const { t } = useTranslation();
  const { actorSettingId, serviceId } = useParams<PathsParams[PathsE.UPDATE_ACTOR_SETTING]>();

  const { data: currentService } = useGetOneService({
    pathParams: { serviceId },
    params: undefined
  });
    
  const { useGetOneActorSetting, useUpdateOneActorSetting } = useGet3thPartyServices({
    domain: currentService?.domain,
    token: currentService?.token,
  });
  const { mutate, isPending: isLoadingUpdate } = useUpdateOneActorSetting({
    pathParams: { actorSettingId, serviceId },
    params: undefined,
  });
  const { data, isLoading } = useGetOneActorSetting({
    pathParams: { actorSettingId },
    params: undefined,
  });

  const { gray800, white, gray100, brand500, brand400 } = useGetColors();
  
  const methods = useForm<UpdateActorSettingFormT>({
    defaultValues: getUpdateActorSettingValues(data),
    resolver: updateActorSettingResolver,
    mode: "onChange",
  });
  const { formState: { isValid }, trigger } = methods;

  useEffect(() => {
    trigger();
  }, []);
  
  useEffect(() => {
    methods.reset(data);
  }, [data])

  const onSubmit: SubmitHandler<UpdateActorSettingFormT> = (values) => {
    mutate(values, { onError: (error) => fillFormErrors(methods.setError, error) })
  };

  const breadcrumbs = [
    {
      path: PathsE.HOME,
      text: t('general.home'),
    },
    {
      path: PathsE.SETTINGS,
      text: t('services.settings'),
      pathsParams: {
        serviceId,
      },
    },
    {
      path: PathsE.SHOW_ACTOR_SETTING,
      text: t('services.viewActorSetting'),
      pathsParams: {
        actorSettingId: data?.id,
        serviceId,
      },
    },
  ];

  if (isLoading) return <Loader />;

  return (
    <Box>
      <PageTitle
        title={t('services.editSetting')}
        breadcrumbs={breadcrumbs}
        backUrl={{
          path: PathsE.SHOW_ACTOR_SETTING,
          pathsParams: {
            actorSettingId: data?.id,
            serviceId,
          }
        }}
      />
      <Card mb="20px" borderRadius="20px" px="30px">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Form />
            <HSeparator my="20px" mx="-20px" width="calc(100% + 40px)" />
            <Link<PathsParams[PathsE.SHOW_ACTOR_SETTING]> path={PathsE.SHOW_ACTOR_SETTING} pathsParams={{ actorSettingId, serviceId }}>
              <Button
                color={gray800}
                bgColor={gray100}
                borderRadius="8px"
                w="100px"
              >
                {t('buttons.cancel')}
              </Button>
            </Link>
            <Button
              color={white}
              _hover={{
                bgColor: brand400,
              }}
              bgColor={brand500}
              borderRadius="8px"
              ml="20px"
              isLoading={isLoadingUpdate}
              w="100px"
              type="submit"
              isDisabled={!isValid}
            >
              {t('buttons.edit')}
            </Button>
          </form>
        </FormProvider>
      </Card>
    </Box>
  );
};

export default Update;
